import React, { ChangeEvent } from 'react';
import { Card } from 'components/Card';
import { InfoTooltip } from 'components/InfoTooltip';
import { SwitchControl } from 'components/SwitchControl';
import { Box, Stack, Typography } from '@mui/material';

import optimizedTargetingImage from 'assets/optimized-targeting.svg';
import { CheckCircleOutlined } from '@mui/icons-material';
import { useCampaignForm } from 'hooks';
import { CampaignFields } from 'types';
import { TargetingGuidance } from './TargetingGuidance';

export const Targeting = () => {
  const { watch, setValue, onFieldChange } = useCampaignForm();

  const targetingGuidanceMode = watch(CampaignFields.targetingGuidanceMode);

  const handleTargetingGuidanceShow = (e: ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    const newGuidanceMode = checked ? 'global' : 'none';

    setValue(CampaignFields.targetingGuidanceMode, newGuidanceMode);
    onFieldChange(CampaignFields.targetingGuidanceMode, true);
  };

  return (
    <Stack spacing={{ xs: 6, md: 10 }}>
      <Card>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          alignItems={{ xs: 'center', md: 'stretch' }}
          spacing={{ xs: 4, md: 10 }}
        >
          <Stack spacing={2}>
            <Box display="flex" sx={{ alignItems: { xs: 'flex-start', md: 'center' } }}>
              <CheckCircleOutlined color="primary" sx={{ mr: 2, transform: 'translateY(2px)' }} />
              <Typography variant="subtitle1" color="primary" sx={{ mr: 2 }}>
                Optimierte Zielgruppen sind bereits eingerichtet
              </Typography>
              <InfoTooltip
                placement="top"
                text={
                  <Stack spacing={2}>
                    <Typography variant="body2">
                      Optimierte Zielgruppen helfen dir, deine Kampagnenziele effizient zu erreichen, indem sie Daten
                      von deiner Kampagnen-Website und den Creatives nutzen.
                    </Typography>
                    <Typography variant="body2">
                      So erreichst du relevante Zielgruppen mit hoher Wahrscheinlichkeit für eine Conversion.
                    </Typography>
                    <Typography variant="body2">
                      Berechnet werden nur Interaktionen wie Klicks auf deine Banner oder Engagements auf der
                      Kampagnen-Website. Mehr dazu im nächsten Schritt.
                    </Typography>
                  </Stack>
                }
              />
            </Box>

            <Typography variant="body1">
              Die optimierten Zielgruppen sind automatisch für alle Kampagnen aktiviert. Es nutzt die Informationen
              deiner Kampagnen-Website und Creatives, um Zielgruppen zu finden, die deine Kampagnenziele effizient
              erreichen können.
            </Typography>
          </Stack>
          <img src={optimizedTargetingImage} alt="Optimized targeting" style={{ maxWidth: '140px' }} />
        </Stack>
      </Card>

      <Stack spacing={{ xs: 4, md: 6 }}>
        {/* switch have 12px padding arround, 20 - 12 = 8 */}
        <Card sx={{ pl: 2 }}>
          <SwitchControl
            htmlId="targeting-enable-guidance-switch"
            checked={targetingGuidanceMode !== 'none'}
            onChange={handleTargetingGuidanceShow}
            label={{
              text: 'Zusätzlich deine eigene Zielgruppe hinzufügen (Optional)',
              variant: 'subtitle1',
            }}
            tooltip={{
              placement: 'bottom',
              text: (
                <Stack spacing={2}>
                  <Typography variant="body2">
                    Zusätzlich zu den optimierten Zielgruppen kannst du deine eigene Zielgruppe hinzufügen um das
                    Targeting zu verfeinern. Die optimierten Zielgruppen bleiben dabei aktiv.
                  </Typography>
                  <Typography variant="body2">
                    Diese Zusatzoption ist kostenlos. Berechnet werden nur Interaktionen wie Klicks auf deine Banner
                    oder Engagements auf der Kampagnen-Website. Mehr dazu im nächsten Schritt.
                  </Typography>
                </Stack>
              ),
            }}
          />
        </Card>
        {targetingGuidanceMode !== 'none' ? <TargetingGuidance /> : null}
      </Stack>
    </Stack>
  );
};
